import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result?.user?.role === 'tutor') {
        navigate('/tutor/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #D1FAE5 0%, #FEF3C7 100%)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="p-8 rounded-3xl shadow-lg border-2 border-slate-200">
          <div className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-32 h-32 mx-auto mb-4 object-contain"
              data-testid="login-mascot-image"
            />
            <h1 className="text-4xl font-heading font-bold text-emerald-600 tracking-tight" data-testid="login-title">
              Prof.Scaly
            </h1>
            <p className="text-slate-600 mt-2" data-testid="login-subtitle">Welcome back! Ready to practice?</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-3 flex items-start gap-2" data-testid="login-error">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-slate-700 font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-2xl border-2 focus:border-emerald-500 focus:ring-emerald-200"
                placeholder="your@email.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 rounded-2xl border-2 focus:border-emerald-500 focus:ring-emerald-200"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-6 text-lg font-bold tactile-button border-emerald-700"
              data-testid="login-submit-button"
            >
              {loading ? 'Logging in...' : 'Log In'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-emerald-600 font-bold hover:text-emerald-700"
                data-testid="login-forgot-link"
              >
                Forgot password?
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600">
              Don't have an account?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-emerald-600 font-bold hover:text-emerald-700"
                data-testid="login-register-link"
              >
                Register here
              </button>
            </p>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default LoginPage;